import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('device');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load theme preference from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('pitchlab-theme') || 'device';
    setTheme(savedTheme);
    applyTheme(savedTheme);
    setIsLoading(false);
  }, []);

  // Apply theme to document
  const applyTheme = (themeValue) => {
    const html = document.documentElement;
    let shouldBeDark = false;

    if (themeValue === 'dark') {
      shouldBeDark = true;
    } else if (themeValue === 'light') {
      shouldBeDark = false;
    } else {
      // device preference
      shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }

    setIsDarkMode(shouldBeDark);
    
    if (shouldBeDark) {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  };

  // Handle system preference changes
  useEffect(() => {
    if (theme !== 'device') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      applyTheme('device');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const toggleTheme = () => {
    let newTheme;
    if (theme === 'light') {
      newTheme = 'dark';
    } else if (theme === 'dark') {
      newTheme = 'device';
    } else {
      newTheme = 'light';
    }
    
    setTheme(newTheme);
    localStorage.setItem('pitchlab-theme', newTheme);
    applyTheme(newTheme);
  };

  const setThemeMode = (mode) => {
    setTheme(mode);
    localStorage.setItem('pitchlab-theme', mode);
    applyTheme(mode);
  };

  return (
    <ThemeContext.Provider value={{ theme, isDarkMode, toggleTheme, setThemeMode, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
